import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import './ExcelExportModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileExcel,
  faTimes,
  faCheck,
  faToggleOn,
  faToggleOff,
  faDownload,
  faTag,
  faTshirt,
  faIdCard,
  faInfoCircle,
  faSort
} from '@fortawesome/free-solid-svg-icons';

const ExcelExportModal = ({ isOpen, onClose, data, categories, teams, currentAuction }) => {
  const [excelType, setExcelType] = useState('1'); // 1: Normal, 2: Unsold, 3: Final
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [savingPreference, setSavingPreference] = useState(false);
  
  // Column visibility states
  const [columns, setColumns] = useState({
    playerNumber: true,
    name: true,
    mobile: true,
    age: true,
    dob: true,
    category: true,
    spec1: true,
    spec2: true,
    spec3: true,
    tshirt: true,
    trouser: true,
    jerseyName: true,
    jerseyNumber: true,
    baseBid: false,
    image: true,
    detail: true,
    team: true,
    points: true,
    fname: true
  });

  const columnGroups = {
    "Basic Information": {
      icon: faIdCard,
      columns: ["playerNumber", "name", "mobile", "age", "dob", "category"]
    },
    "Specifications": {
      icon: faTag,
      columns: ["spec1", "spec2", "spec3", "detail"]
    },
    "Jersey Details": {
      icon: faTshirt,
      columns: ["tshirt", "trouser", "jerseyName", "jerseyNumber"]
    },
    "Additional Info": {
      icon: faInfoCircle,
      columns: ["baseBid", "image", "team", "points", "fname"]
    }
  };

  const handleColumnToggle = (columnName) => {
    setColumns(prev => ({
      ...prev,
      [columnName]: !prev[columnName]
    }));
  };

  const handleGroupToggle = (groupColumns) => {
    const allChecked = groupColumns.every(col => columns[col]);
    setColumns(prev => {
      const newState = { ...prev };
      groupColumns.forEach(col => {
        newState[col] = !allChecked;
      });
      return newState;
    });
  };

  const handleSavePreference = () => {
    setSavingPreference(true);
    // Mock saving preferences
    setTimeout(() => {
      setSavingPreference(false);
    }, 1000);
  };

  const generateExcel = () => {
    if (!data?.data?.list) {
      console.error("No player data available");
      return;
    }

    const excelData = [];
    
    // Generate headers
    const headers = [];
    if (columns.playerNumber) headers.push('P_no');
    headers.push('Name'); // Name is always included
    if (columns.mobile) headers.push('Mobile');
    if (columns.age) headers.push('Age');
    if (columns.dob) headers.push('DOB');
    if (columns.category) headers.push('Category');
    if (columns.spec1) headers.push('Specification 1');
    if (columns.spec2) headers.push('Specification 2');
    if (columns.spec3) headers.push('Specification 3');
    if (columns.tshirt) headers.push('Tshirt');
    if (columns.trouser) headers.push('Trouser');
    if (columns.jerseyName) headers.push('Jersey_name');
    if (columns.jerseyNumber) headers.push('Jersey_no');
    if (columns.baseBid) headers.push('Base_bid');
    if (columns.image) headers.push('Image');
    if (columns.detail) headers.push('Detail');
    if (excelType === '3') {
      if (columns.team) headers.push('Team');
      if (columns.points) headers.push('Points');
    }

    excelData.push(headers);

    // Filter and transform data based on excel type
    let filteredData = data.data.list;
    if (excelType === '2') {
      filteredData = data.data.list.filter(player => player.tmid === "0");
    } else if (excelType === '3') {
      filteredData = data.data.list.filter(player => player.tmid !== "0");
    }

    // Generate rows
    filteredData.forEach(player => {
      const row = [];
      if (columns.playerNumber) row.push(player.pcode);
      row.push(player.pname);
      if (columns.mobile) row.push(player.mob || '-');
      if (columns.age) row.push(player.age !== "0" ? player.age : '-');
      if (columns.dob) row.push(player.dob || '-');
      if (columns.category) {
        const category = categories?.find(cat => cat.cid === player.catid);
        row.push(category ? category.cname : '-');
      }
      if (columns.spec1) row.push(player.spcf1 || '-');
      if (columns.spec2) row.push(player.spcf2 || '-');
      if (columns.spec3) row.push(player.spcf3 || '-');
      if (columns.tshirt) row.push(player.tshirt || '-');
      if (columns.trouser) row.push(player.trouser || '-');
      if (columns.jerseyName) row.push(player.jrnm || '-');
      if (columns.jerseyNumber) row.push(player.jrno || '-');
      if (columns.baseBid) row.push(player.base || '-');
      if (columns.image) row.push(data.data.path + player.img);
      if (columns.detail) row.push(player.detail || '-');
      if (excelType === '3') {
        if (columns.team) {
          const team = teams?.find(t => t.tid === player.tmid);
          row.push(team ? team.name : '-');
        }
        if (columns.points) row.push(player.points || '-');
      }
      
      excelData.push(row);
    });

    // Create and download Excel file
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Players");

    // Auto-size columns
    const colWidths = excelData[0].map((_, i) => ({
      wch: Math.max(...excelData.map(row => row[i]?.toString().length || 0))
    }));
    ws['!cols'] = colWidths;

    // Generate filename based on type
    let filename = `${currentAuction.aid}-${currentAuction.name}_PLIST`;
    if (excelType === '2') filename += '_UNSOLD';
    if (excelType === '3') filename += '_FINAL';
    filename += '.xlsx';

    XLSX.writeFile(wb, filename);
    onClose();
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case '1': return 'All Players';
      case '2': return 'Unsold Players';
      case '3': return 'Sold Players';
      default: return 'All Players';
    }
  };

  const getRecordCount = () => {
    if (!data?.data?.list) return 0;
    
    if (excelType === '2') {
      return data.data.list.filter(player => player.tmid === "0").length;
    } else if (excelType === '3') {
      return data.data.list.filter(player => player.tmid !== "0").length;
    }
    return data.data.list.length;
  };

  if (!isOpen) return null;

  return (
    <div className="excel-modal-overlay">
      <div className="excel-modal">
        <div className="modal-header">
          <div className="modal-title">
            <FontAwesomeIcon icon={faFileExcel} className="excel-icon" />
            <h2>Export to Excel</h2>
          </div>
          {/* <button className="close-button" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button> */}
        </div>
        
        <div className="modal-content">
          <div className="export-panel">
            <div className="export-details">
              <h3>Export Configuration</h3>
              <div className="export-summary">
                <div className="export-summary-row">
                  <span className="summary-label">Export type:</span>
                  <span className="summary-value">{getTypeLabel(excelType)}</span>
                </div>
                <div className="export-summary-row">
                  <span className="summary-label">Records:</span>
                  <span className="summary-value">{getRecordCount()} players</span>
                </div>
                <div className="export-summary-row">
                  <span className="summary-label">Columns:</span>
                  <span className="summary-value">{Object.values(columns).filter(Boolean).length} columns selected</span>
                </div>
              </div>
            </div>
            
            <div className="export-options">
              <h4>Export Type</h4>
              <div className="option-cards">
                <div 
                  className={`option-card ${excelType === '1' ? 'active' : ''}`}
                  onClick={() => setExcelType('1')}
                >
                  <div className="option-icon">
                    <FontAwesomeIcon icon={faSort} />
                  </div>
                  <div className="option-details">
                    <h5>All Players</h5>
                    <p>Export data for all players in the system</p>
                  </div>
                  {excelType === '1' && (
                    <div className="option-selected">
                      <FontAwesomeIcon icon={faCheck} />
                    </div>
                  )}
                </div>
                
                <div 
                  className={`option-card ${excelType === '2' ? 'active' : ''}`}
                  onClick={() => setExcelType('2')}
                >
                  <div className="option-icon unsold-icon">
                    <FontAwesomeIcon icon={faSort} />
                  </div>
                  <div className="option-details">
                    <h5>Unsold Players</h5>
                    <p>Export data for players who haven't been sold</p>
                  </div>
                  {excelType === '2' && (
                    <div className="option-selected">
                      <FontAwesomeIcon icon={faCheck} />
                    </div>
                  )}
                </div>
                
                <div 
                  className={`option-card ${excelType === '3' ? 'active' : ''}`}
                  onClick={() => setExcelType('3')}
                >
                  <div className="option-icon sold-icon">
                    <FontAwesomeIcon icon={faSort} />
                  </div>
                  <div className="option-details">
                    <h5>Sold Players</h5>
                    <p>Export data for players who have been sold</p>
                  </div>
                  {excelType === '3' && (
                    <div className="option-selected">
                      <FontAwesomeIcon icon={faCheck} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="columns-toggle">
            <div className="toggle-header" onClick={() => setShowAdvanced(!showAdvanced)}>
              <h4>Advanced Column Selection</h4>
              <FontAwesomeIcon icon={showAdvanced ? faToggleOn : faToggleOff} className={`toggle-icon ${showAdvanced ? 'active' : ''}`} />
            </div>
            
            {showAdvanced && (
              <div className="columns-container">
                {Object.entries(columnGroups).map(([groupName, group]) => (
                  <div key={groupName} className="column-group-card">
                    <div 
                      className="group-header"
                      onClick={() => handleGroupToggle(group.columns)}
                    >
                      <div className="group-title">
                        <FontAwesomeIcon icon={group.icon} className="group-icon" />
                        <h5>{groupName}</h5>
                      </div>
                      <div className={`group-checkbox ${group.columns.every(col => columns[col]) ? 'checked' : ''}`}>
                        <FontAwesomeIcon icon={faCheck} className="check-icon" />
                      </div>
                    </div>
                    <div className="group-options">
                      {group.columns.map(columnName => (
                        <div key={columnName} className="column-option">
                          <div 
                            className={`option-checkbox ${columns[columnName] ? 'checked' : ''}`}
                            onClick={() => handleColumnToggle(columnName)}
                          >
                            {columns[columnName] && <FontAwesomeIcon icon={faCheck} className="check-icon" />}
                          </div>
                          <label onClick={() => handleColumnToggle(columnName)}>
                            {columnName.replace(/([A-Z])/g, ' $1').trim()}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                <div className="preference-row">
                  <button 
                    className={`save-preference ${savingPreference ? 'saving' : ''}`} 
                    onClick={handleSavePreference}
                    disabled={savingPreference}
                  >
                    {savingPreference ? 'Saving...' : 'Save as Default Preferences'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-download" onClick={generateExcel}>
            <FontAwesomeIcon icon={faDownload} className="download-icon" />
            Download Excel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExcelExportModal; 